import React, { useEffect, useState, useContext } from "react";
import {
  Container,
  Card,
  Table,
  Badge,
  Form,
  Row,
  Col,
  Alert,
  Spinner,
  InputGroup,
  Pagination,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { getAllSystemUsers, SystemUser, UsersResponse } from "../../services/api";
import { useErrorStore } from "../../errorStore";
import { UserContext } from "../../userContext";
import { Search, Person, Hash } from "react-bootstrap-icons";

export default function UsersOverview() {
  const { t } = useTranslation("common");
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<SystemUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [teamsRoleFilter, setTeamsRoleFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [pagination, setPagination] = useState<UsersResponse['pagination'] | null>(null);
  const setErrorMessage = useErrorStore((state) => state.setErrorMessage);
  const currentUser = useContext(UserContext);

  const loadUsers = async (page: number = 1) => {
    try {
      setLoading(true);
      const response = await getAllSystemUsers(page, perPage);
      setUsers(response.data);
      setFilteredUsers(response.data);
      setPagination(response.pagination);
      setCurrentPage(page);
    } catch (error: any) {
      setErrorMessage(t("error.loadingFailed"));
      console.error("Error loading users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers(1);
  }, [perPage]);

  useEffect(() => {
    let filtered = [...users];

    // Textsuche (Name, Username)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const fullname = (user: SystemUser) => `${user.firstname} ${user.lastname}`;
      filtered = filtered.filter(
        (user) =>
          fullname(user).toLowerCase().includes(searchLower) ||
          user.firstname.toLowerCase().includes(searchLower) ||
          user.lastname.toLowerCase().includes(searchLower) ||
          user.username.toLowerCase().includes(searchLower)
      );
    }

    // Rolle filtern
    if (roleFilter) {
      filtered = filtered.filter((user) => user.role === roleFilter);
    }

    // Teams-Rolle filtern
    if (teamsRoleFilter) {
      filtered = filtered.filter((user) =>
        user.teamsRole?.includes(teamsRoleFilter)
      );
    }

    // Nach Namen sortieren
    filtered.sort((a, b) => {
      const nameA = `${a.lastname} ${a.firstname}`.toLowerCase();
      const nameB = `${b.lastname} ${b.firstname}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });

    setFilteredUsers(filtered);
  }, [searchTerm, roleFilter, teamsRoleFilter, users]);

  const getRoleBadge = (role: string) => {
    const variants: { [key: string]: string } = {
      admin: "danger",
      office: "warning",
      teacher: "primary",
      user: "secondary",
    };
    return (
      <Badge bg={variants[role] || "secondary"}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </Badge>
    );
  };

  const getTeamsRoleBadges = (teamsRole: string) => {
    if (!teamsRole) return null;
    const roles = teamsRole.split(",");
    return (
      <div className="d-flex gap-1 flex-wrap">
        {roles.map((role, index) => (
          <Badge key={index} bg="info" className="text-capitalize">
            {role.trim()}
          </Badge>
        ))}
      </div>
    );
  };

  const handlePageChange = (page: number) => {
    loadUsers(page);
  };

  // Check admin access after all hooks
  if (currentUser?.role !== "admin" && currentUser?.teamsRole?.search("admin") === -1) {
    return (
      <Container>
        <Alert variant="danger">{t("accessDenied")}</Alert>
      </Container>
    );
  }

  // Statistiken berechnen (nur für aktuelle Seite)
  const stats = {
    total: pagination?.total || 0,
    currentPageCount: users.length,
    admins: users.filter((u) => u.role === "admin").length,
    offices: users.filter((u) => u.role === "office").length,
    teachers: users.filter((u) => u.role === "teacher").length,
    staff: users.filter((u) => u.teamsRole?.includes("staff")).length,
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center mt-5">
          <Spinner animation="border" />
        </div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <h2>{t("usersOverview.title")}</h2>
      
      {/* Statistik-Karten */}
      <Row className="mb-4">
        <Col sm={6} md={4} lg={2}>
          <Card className="text-center">
            <Card.Body>
              <h3>{stats.total}</h3>
              <small className="text-muted">{t("usersOverview.totalUsers")}</small>
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} md={4} lg={2}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-danger">{stats.admins}</h3>
              <small className="text-muted">{t("usersOverview.admins")}</small>
              <small className="text-muted d-block">(diese Seite)</small>
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} md={4} lg={2}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-warning">{stats.offices}</h3>
              <small className="text-muted">{t("usersOverview.offices")}</small>
              <small className="text-muted d-block">(diese Seite)</small>
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} md={4} lg={2}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">{stats.teachers}</h3>
              <small className="text-muted">{t("usersOverview.teachers")}</small>
              <small className="text-muted d-block">(diese Seite)</small>
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} md={4} lg={2}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-success">{stats.staff}</h3>
              <small className="text-muted">{t("usersOverview.staff")}</small>
              <small className="text-muted d-block">(diese Seite)</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filter */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">{t("usersOverview.filters")}</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={3}>
              <InputGroup>
                <InputGroup.Text>
                  <Search />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder={t("usersOverview.searchPlaceholder") || "Name oder Benutzername suchen..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={2}>
              <Form.Select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="">{t("usersOverview.allRoles")}</option>
                <option value="admin">Admin</option>
                <option value="office">Office</option>
                <option value="teacher">Teacher</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={teamsRoleFilter}
                onChange={(e) => setTeamsRoleFilter(e.target.value)}
              >
                <option value="">{t("usersOverview.allTeamsRoles")}</option>
                <option value="admin">Teams Admin</option>
                <option value="staff">Staff</option>
                <option value="lba">LBA</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
              >
                <option value={10}>10 pro Seite</option>
                <option value={25}>25 pro Seite</option>
                <option value={50}>50 pro Seite</option>
                <option value={100}>100 pro Seite</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <div className="text-muted">
                {t("usersOverview.showingUsers", { count: filteredUsers.length })} 
                {pagination && ` (Seite ${pagination.current_page} von ${pagination.total_pages})`}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Benutzertabelle */}
      <Card className="mb-4">
        <Card.Body className="p-0">
          <Table striped hover responsive className="mb-0">
            <thead>
              <tr>
                <th>
                  <Person className="me-1" />
                  {t("usersOverview.name")}
                </th>
                <th>{t("usersOverview.username")}</th>
                <th>{t("usersOverview.role")}</th>
                <th>{t("usersOverview.teamsRoles")}</th>
                <th>
                  <Hash className="me-1" />
                  {t("usersOverview.departments")}
                </th>
                <th>{t("usersOverview.fachbereiche")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user.username}>
                    <td>
                      <strong>{user.firstname} {user.lastname}</strong>
                    </td>
                    <td>
                      <small className="text-muted">{user.username}</small>
                    </td>
                    <td>{getRoleBadge(user.role)}</td>
                    <td>{getTeamsRoleBadges(user.teamsRole)}</td>
                    <td>
                      {user.departments.length > 0 ? (
                        <small>{user.departments.join(", ")}</small>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {user.fachbereiche.length > 0 ? (
                        <small>{user.fachbereiche.join(", ")}</small>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    {t("usersOverview.noUsersFound")}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div className="d-flex justify-content-center">
          <Pagination>
            <Pagination.First 
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            
            {/* Seitenzahlen */}
            {(() => {
              const pages = [];
              const maxPages = 5;
              let startPage = Math.max(1, currentPage - Math.floor(maxPages / 2));
              let endPage = Math.min(pagination.total_pages, startPage + maxPages - 1);
              
              if (endPage - startPage < maxPages - 1) {
                startPage = Math.max(1, endPage - maxPages + 1);
              }
              
              if (startPage > 1) {
                pages.push(
                  <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                  </Pagination.Item>
                );
                if (startPage > 2) {
                  pages.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
                }
              }
              
              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => handlePageChange(i)}
                  >
                    {i}
                  </Pagination.Item>
                );
              }
              
              if (endPage < pagination.total_pages) {
                if (endPage < pagination.total_pages - 1) {
                  pages.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
                }
                pages.push(
                  <Pagination.Item 
                    key={pagination.total_pages} 
                    onClick={() => handlePageChange(pagination.total_pages)}
                  >
                    {pagination.total_pages}
                  </Pagination.Item>
                );
              }
              
              return pages;
            })()}
            
            <Pagination.Next 
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.total_pages}
            />
            <Pagination.Last 
              onClick={() => handlePageChange(pagination.total_pages)}
              disabled={currentPage === pagination.total_pages}
            />
          </Pagination>
        </div>
      )}
    </Container>
  );
}